import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Field, Formik, Form } from "formik";
import { capitalize } from "lodash";
import { useCallback, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import Modal from "react-modal";
import { useSelector, useDispatch } from "react-redux";
import type {} from "redux-thunk/extend-redux";
import { useNavigate } from "react-router-dom";

import { User, Case, Specimen, CaseReport } from "pwapi";

import { CaseDiagnosis } from "custom-models";

import {
  cancelEditScreenDiagnoses,
  cancelModifyReportProcedure,
  createNote,
  openDocument,
  patchCaseStatus,
  requestSignout,
} from "shared/actions/caseDetailActions";
import { caseSearchToURL, navigateCaseList } from "shared/actions/casesActions";
import ReviewList, { sortByLatest } from "shared/components/ReviewList";
import { useLISFinalizedDiagnosis } from "shared/hooks/useDiagnosis";
import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { SlideDetailState } from "shared/reducers/slideDetailReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";
import { isCaseTCOnly } from "shared/utils/caseUtils";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import TextareaInput from "components/customInputs/TextareaInput";
import Header from "components/header/Header";
import RecallReportButton from "components/RecallReportButton";
import ApplicationState from "store/ApplicationState";

import CaseInfo from "./CaseInfo";
import { DialogEnum } from "./dialogs/Dialog";
import {
  canSignoutCase,
  getFinalizedReportCaseDocument,
  getReviewStatus,
  isDiagnosable,
  isModifyingReport,
  isScreened,
} from "./dialogs/utils";
import RequestScreenButton from "./RequestScreenButton";
import { SlideAdminLink } from "./SlideHeader/AdminLink";
import { HelpButton } from "./SlideHeader/HelpButton";
import { SlideLabelAndSource } from "./SlideHeader/SlideLabelAndSource";
import LinkButton from "../LinkButton";
import Spinner from "../Spinner";

import "./SlideHeader.css";

export type SlideHeaderProps = {
  caseDetailObject: CaseDetailState;
  slideDetailObject: SlideDetailState;
  specimen: Specimen;
  setPatientModal: (show: boolean) => void;
  setWorkflowModal: (show: boolean) => void;
  setPreferencesModal: (show: boolean) => void;
  dialog: DialogEnum | null;
  setDialog: Function;
};

const SlideHeader = (props: SlideHeaderProps) => {
  const {
    caseDetailObject,
    slideDetailObject,
    setPatientModal,
    setWorkflowModal,
    setPreferencesModal,
    dialog,
    setDialog,
    specimen,
  } = props;
  const dispatch = useDispatch();
  const features: FeatureFlags = useSelector(
    ({ sys }: ApplicationState) => sys.features
  );
  const currentUser: User | undefined = useSelector(
    ({ auth }: ApplicationState) => auth.user
  );

  const userCanSeeReviews =
    features?.qaReviewsInViewer &&
    (currentUser?.is_superuser ||
      ["lab_admin", "dermpath", "admin"].includes(
        currentUser?.user_type || ""
      ));

  const {
    caseIdList,
    caseIdListPending,
    count: totalRows,
    currentCaseSearch: caseSearch,
  } = useSelector(({ cases }: ApplicationState) => cases);
  const navigate = useNavigate();

  // State variable: ID of case where a prev/next case button just got clicked
  const [
    caseIdWithPrevNextButtonsDisabled,
    setCaseIdWithPrevNextButtonsDisabled,
  ] = useState<number | undefined>(undefined);
  const [reviewsOpen, setReviewsOpen] = useState<boolean>(false);
  const [reviewSaveDisabled, setReviewSaveDisabled] = useState<boolean>(false);
  const [signingOut, setSigningOut] = useState<boolean>(false);

  let enablePrevCase = false;
  let enableNextCase = false;

  if (!caseDetailObject || !caseDetailObject.id) {
    enablePrevCase = false;
    enableNextCase = false;
  } else if (caseIdWithPrevNextButtonsDisabled !== caseDetailObject.id) {
    const currentCaseResultIndex = caseIdList.indexOf(caseDetailObject.id || 0);

    if (currentCaseResultIndex > 0) {
      enablePrevCase = true;
    }
    if (currentCaseResultIndex < totalRows - 1) {
      enableNextCase = true;
    }
  }

  const finalDiagnosis: CaseDiagnosis | undefined = caseDetailObject
    ? useLISFinalizedDiagnosis(caseDetailObject.case_diagnosis)
    : undefined;

  const stepThroughCaseList = useCallback(
    (forward: boolean) => {
      // Disable the button after it's been clicked on, for the current case ID.
      setCaseIdWithPrevNextButtonsDisabled(caseDetailObject?.id);
      dispatch(navigateCaseList(navigate, forward));
    },
    [caseDetailObject]
  );

  const finalizeCase = useCallback(() => {
    if (
      features.caseFinalizeKey &&
      caseDetailObject &&
      caseDetailObject.status !== Case.status.FINALIZED
    ) {
      if (features?.technicalReportsOnly && isCaseTCOnly(caseDetailObject)) {
        dispatch(
          requestSignout(
            caseDetailObject.id!,
            navigate,
            undefined,
            CaseReport.report_type.TECHNICAL,
            undefined
          )
        );
      } else {
        dispatch(
          patchCaseStatus({
            id: caseDetailObject.id,
            status: Case.status.FINALIZED,
          })
        );
      }
    }
    setSigningOut(true);
  }, [caseDetailObject, features?.technicalReportsOnly]);

  useHotkeys(
    "p,shift+p",
    () => {
      if (enablePrevCase) {
        stepThroughCaseList(false);
      }
    },
    { scopes: ["viewerHotkeys"] },
    [enablePrevCase, navigate]
  );
  useHotkeys(
    "n,shift+n",
    () => {
      if (!caseIdListPending && enableNextCase) {
        stepThroughCaseList(true);
      }
    },
    { scopes: ["viewerHotkeys"] },
    [enableNextCase, caseIdListPending, navigate]
  );
  useHotkeys(
    "shift+f",
    () => {
      !signingOut && finalizeCase();
    },
    { scopes: ["viewerHotkeys"] },
    [signingOut]
  );

  const isModifying = isModifyingReport(
    caseDetailObject,
    features,
    currentUser
  );
  const canSignout = canSignoutCase(
    caseDetailObject,
    currentUser,
    features?.signOutInViewer
  );
  const hasBeenScreened = isScreened(
    caseDetailObject.specimenDetail[0]?.diagnoses
  );
  const caseReviewStatus = getReviewStatus(caseDetailObject);

  const onHandleCancelReportModify = () => {
    caseDetailObject?.id &&
      dispatch(cancelModifyReportProcedure(caseDetailObject.id));
  };

  const onHandleCancelScreenEdit = () => {
    caseDetailObject.id &&
      dispatch(cancelEditScreenDiagnoses(caseDetailObject.id));
  };

  const final_report_id =
    caseDetailObject.source === Case.source.PW
      ? getFinalizedReportCaseDocument(caseDetailObject)?.id
      : finalDiagnosis?.finalized_report_id;

  const onSaveQAReview = (payload: any) => {
    dispatch(createNote(payload)).then(() => {
      setReviewsOpen(false);
      setReviewSaveDisabled(false);
    });
  };

  const handleSubmit = (values: any) => {
    setReviewSaveDisabled(true);

    const payload = {
      parentLookupCaseId: caseDetailObject.id,
      note: values.note,
    };

    onSaveQAReview(payload);
  };

  return (
    <Header
      logoType="simple"
      logoLink={caseSearchToURL(caseSearch, currentUser)}
      adminLink={
        <SlideAdminLink
          caseId={caseDetailObject.id}
          slideId={slideDetailObject.id!}
        />
      }
      helper={
        <div className="SlideHeader__helper">
          <HelpButton dialog={props.dialog} setDialog={props.setDialog} />
        </div>
      }
    >
      <div className="SlideHeader">
        <CaseInfo
          caseDetail={caseDetailObject}
          slideId={slideDetailObject.id}
          setPatientModal={setPatientModal}
          setPreferencesModal={setPreferencesModal}
        />
        {!features?.mrmcStudy && (
          <div className="SlideInfo">
            <SlideLabelAndSource
              specimen={specimen}
              slideDetailObject={slideDetailObject}
            />
            <div>
              {features?.changeAssignment && (
                <p>
                  <a
                    onClick={
                      features?.caseReassignment
                        ? () => setWorkflowModal(true)
                        : undefined
                    }
                    className="SlideHeader__assignment"
                  >
                    {caseDetailObject.active_assignment && (
                      <>
                        {caseDetailObject.active_assignment?.first_name}
                        &nbsp;
                        {caseDetailObject.active_assignment?.last_name}{" "}
                      </>
                    )}
                    {features?.caseReassignment && (
                      <FontAwesomeIcon icon="cog" />
                    )}
                  </a>
                </p>
              )}
              {userCanSeeReviews && (
                <>
                  <LinkButton
                    color="steel"
                    onClick={() => {
                      setReviewsOpen(true);
                    }}
                  >
                    <FontAwesomeIcon icon="sticky-note" title="QA Reviews" />
                    {`(${caseDetailObject.qa_reviews_list.length})`}
                  </LinkButton>
                  <Modal
                    isOpen={reviewsOpen}
                    onRequestClose={() => setReviewsOpen(false)}
                    contentLabel="QA Reviews"
                    className="Modal"
                    overlayClassName="Overlay"
                  >
                    <Formik
                      initialValues={{ note: "" }}
                      enableReinitialize
                      onSubmit={(values) => handleSubmit(values)}
                    >
                      {({ values }) => (
                        <Form className="reviewModal__form">
                          <h4>QA Review Logs</h4>
                          <ReviewList
                            reviews={sortByLatest(
                              caseDetailObject.qa_reviews_list
                            )}
                          />
                          <Field name="note">
                            {({ field }: any) => (
                              <TextareaInput
                                label="Note"
                                rows={4}
                                placeholder="Add a case note. These notes are visible to internal staff only and are not client-facing."
                                {...field}
                              />
                            )}
                          </Field>
                          <footer className="reviewModal__footer">
                            <PathologyWatchButton
                              type="submit"
                              disabled={!values.note || reviewSaveDisabled}
                            >
                              Add Note
                            </PathologyWatchButton>
                            <PathologyWatchButton
                              onClick={() => setReviewsOpen(false)}
                            >
                              Cancel
                            </PathologyWatchButton>
                          </footer>
                        </Form>
                      )}
                    </Formik>
                  </Modal>
                </>
              )}
              {features?.qaReviews &&
                caseDetailObject.source === Case.source.PW &&
                caseDetailObject.accession_prefix?.is_diagnostic &&
                !caseDetailObject.accessioning_lab?.settings?.skip_qa_review
                  ?.active &&
                (features?.qaReviewsInViewer ||
                  !caseDetailObject.qa_reviews?.[0]?.reviewed) &&
                [Case.status.ACTIVE, Case.status.WORKING].includes(
                  caseDetailObject.status!
                ) && (
                  <>
                    <div className="SlideHeader__review">
                      {!caseReviewStatus && (
                        <p style={{ color: "var(--red)" }}>
                          <FontAwesomeIcon
                            icon="exclamation-circle"
                            color="var(--red)"
                            title="Case not reviewed by QA"
                          />{" "}
                          {caseReviewStatus === null
                            ? "Missing QA Review"
                            : "Case Held"}
                        </p>
                      )}
                    </div>
                  </>
                )}
            </div>
          </div>
        )}
        <nav className="SlideHeader__content SlideHeader__nav">
          {!features?.mrmcStudy && totalRows > 0 && (
            <LinkButton
              nocolor
              className={"nav__control nav__link skip-next-case"}
              title="Next Case"
              disabled={
                caseIdListPending ||
                !enableNextCase ||
                caseIdWithPrevNextButtonsDisabled === caseDetailObject.id
              }
              onClick={() => {
                if (enableNextCase) {
                  stepThroughCaseList(true);
                }
              }}
            >
              {caseIdListPending && <Spinner size="16px" />}
              {features.signOutInViewer && isDiagnosable(caseDetailObject)
                ? "Skip"
                : "Next Case"}
            </LinkButton>
          )}
          {features?.requestScreen && (
            <RequestScreenButton
              caseDetailObject={caseDetailObject}
              slideId={slideDetailObject.id}
              currentUser={currentUser}
              dialog={dialog}
              setDialog={setDialog}
            />
          )}

          {isModifying?.source && (
            <>
              <PathologyWatchButton
                color="red"
                onClick={onHandleCancelReportModify}
              >
                {`Cancel ${
                  isModifying.source === CaseDiagnosis.source.CORRECTION
                    ? "Report Correction"
                    : `${capitalize(isModifying.source)} Report`
                }`}
              </PathologyWatchButton>
              <LinkButton
                title="Preview Report"
                className="nav__control nav__button"
                onClick={() =>
                  navigate(`/cases/${caseDetailObject.id}/preview`)
                }
              >
                Preview
              </LinkButton>
            </>
          )}

          {(caseDetailObject.status === Case.status.ACTIVE ||
            caseDetailObject.status === Case.status.WORKING) &&
            !final_report_id &&
            caseDetailObject.prelim_report_id &&
            !isModifying?.source && (
              <LinkButton
                title="View Prelim Report"
                className="nav__control nav__button"
                onClick={() => {
                  dispatch(
                    openDocument(
                      caseDetailObject.id || 0,
                      caseDetailObject.prelim_report_id || 0
                    )
                  );
                }}
              >
                Preliminary Report
              </LinkButton>
            )}

          {(caseDetailObject.status === Case.status.FINALIZED ||
            caseDetailObject.status === Case.status.ARCHIVED) &&
            !isModifying?.source &&
            final_report_id && (
              <LinkButton
                title="View Final Report"
                className="nav__control nav__button"
                onClick={() => {
                  dispatch(
                    openDocument(caseDetailObject.id || 0, final_report_id)
                  );
                }}
              >
                Report
              </LinkButton>
            )}

          {!features?.mrmcStudy && features?.signOutInViewer && (
            <>
              {canSignout &&
                !features?.screenPerSpecimen &&
                hasBeenScreened &&
                !caseDetailObject.is_screening &&
                caseDetailObject.specimenDetail[0]?.diagnoses.find(
                  (diag) =>
                    diag.user &&
                    diag.user.id === currentUser?.id &&
                    !diag.is_completed
                ) && (
                  <PathologyWatchButton
                    color="red"
                    onClick={onHandleCancelScreenEdit}
                  >
                    Cancel Screen Edit
                  </PathologyWatchButton>
                )}
              {
                <RecallReportButton
                  currentUser={currentUser}
                  caseDetail={caseDetailObject}
                  dialog={dialog}
                  setDialog={setDialog}
                />
              }
              {(isDiagnosable(caseDetailObject) ||
                caseDetailObject.status === Case.status.LIS_PENDING) &&
                (!features?.technicalReportsOnly ||
                  (features?.technicalReportsOnly &&
                    isCaseTCOnly(caseDetailObject))) && (
                  <PathologyWatchButton
                    title="Preview Sign Out"
                    onClick={() =>
                      navigate(`/cases/${caseDetailObject.id}/preview`)
                    }
                  >
                    Preview
                  </PathologyWatchButton>
                )}
            </>
          )}
        </nav>
      </div>
    </Header>
  );
};
export default SlideHeader;
